import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardMedia,
  Typography,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const ScheduledPagePosts = ({ token, pageId }) => {
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (pageId) {
      fetchScheduledPosts(pageId);
    }
  }, [pageId]);

  const fetchScheduledPosts = async (pageId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/user-scheduled-posts/${pageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 200) {
        setScheduledPosts(response.data);
      }
    } catch (error) {
      console.error("Error fetching scheduled posts:", error);
    }
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftChevron(scrollLeft > 0);
      setShowRightChevron(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -400, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll(); // Check initial state
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [scheduledPosts]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    // Facebook returns seconds, sometimes ms
    const date = new Date(timestamp * 1000 || timestamp); 
    return date.toLocaleString();
  };

  // Don't render anything if there are no scheduled posts
  if (!scheduledPosts || scheduledPosts.length === 0) return null;

  return (
    <Box sx={{ position: "relative" }}>
      {/* Left Chevron */}
      {showLeftChevron && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            position: "absolute",
            left: -20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            backgroundColor: "background.paper",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      {/* Scrollable Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          pl: showLeftChevron ? 4 : 0,
          pr: showRightChevron ? 4 : 0,
        }}
      >
        {scheduledPosts.map((post) => (
          <Card
            key={post._id || post.id}
            sx={{
              flexShrink: 0,
              width: "200px",
              position: "relative",
              overflow: "hidden",
              borderRadius: 2,
            }}
          >
            <CardMedia
              component="img"
              sx={{
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
              image={
                post.thumbnailUrl || 
                (post.mediaUrls && post.mediaUrls[0]) || 
                post.mediaUrl || 
                post.media_url || 
                "/placeholder-image.png"
              }
              alt={post.caption || "Scheduled post image"}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/placeholder-image.png";
              }}
            />
            {/* Overlay with gradient for better text readability */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
                p: 2,
                pt: 3,
              }}
            >
              {/* Caption */}
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  mb: 1,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                {post.caption || "No caption"}
              </Typography>
              
              {/* Timestamp */}
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  display: "block",
                  mb: 1.5,
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                Scheduled for: {formatTimestamp(post.scheduledTime)}
              </Typography>

              {/* Status Badge - Optional but helpful for scheduled posts */}
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.65rem",
                    fontWeight: "bold",
                    textTransform: "uppercase"
                  }}
                >
                  Scheduled
                </Typography>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Right Chevron */}
      {showRightChevron && (
        <IconButton
          onClick={scrollRight}
          sx={{
            position: "absolute",
            right: -20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            backgroundColor: "background.paper",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default ScheduledPagePosts;

